import { useState, useEffect } from "react";
import { PageErrorState } from '@/components/shared/PageErrorState';
import { useTranslation } from 'react-i18next';
import InventoryTable from "@/features/inventory/components/InventoryTable";
import AddStockModal from "@/features/inventory/components/AddStockModal";
import { useInventory } from "@/features/inventory/hooks/useInventory";
import type { Product } from "@/features/inventory/types";
import ErrorBoundary from "@/components/ErrorBoundary";
import BulkStockUploadModal from "@/features/inventory/components/BulkStockUploadModal";
import OnboardingModal from "@/components/onboarding/OnboardingModal";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/hooks/use-role";
import { useOrganization } from "@/contexts/OrganizationContext";
import { PageErrorBoundary } from "@/components/fallbacks";
import { PageLoader } from "@/components/loading/PageLoader";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { BackToTopPill } from "@/components/BackToTopPill";
import { usePreprintPermission } from "@/hooks/usePreprintPermission";
import { useConnectedClientsAtLocations } from "@/features/inventory/hooks/useConnectedClients";
import { useOwnedFloorIds } from "@/features/inventory/hooks/useOwnedFloorIds";
import {
  WHO_ANY,
  whoToListInput,
  type WhoCanUseFilter,
} from "@/features/base-garments/lib/base-garments-url";

const Inventory = () => {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  // ace/trims-ux: server-side type filter (POD / PAP / Trims). Lives here
  // (not inside InventoryTable) so it can drive the actual data fetch via
  // useInventory -> useInventoryQueries -> the tRPC query input.
  const [productionTypeFilter, setProductionTypeFilter] = useState<'PAP' | 'POD' | 'TRIM' | undefined>(undefined);
  // BLU-1394 F25: "Who can use it" for blanks. A 3PL org only (no clients,
  // nothing to tell apart); a query input like the type filter, so the server
  // filters the whole set and the table pages what it returns.
  const { organization, isLoading: orgLoading } = useOrganization();
  const is3pl = organization?.is_3pl_provider === true;
  const [whoFilter, setWhoFilter] = useState<WhoCanUseFilter>(WHO_ANY);
  // The clients across every floor the org owns: the filter list and the
  // badge names. Per-floor cached and shared with Products and Base garments.
  const ownedLocationIds = useOwnedFloorIds(is3pl);
  const { clients: whoClients } = useConnectedClientsAtLocations(ownedLocationIds);
  // BLU-1189: this is the page's ONLY inventory query. `downloadStockCsv` /
  // `moveToUnavailable` / `moveToAvailable` are threaded down to the table
  // rather than pulled from a second instance mounted inside it.
  const {
    products,
    loading,
    error,
    addStock,
    setStock,
    refreshInventory,
    selectedLocation,
    downloadStockCsv,
    moveToUnavailable,
    moveToAvailable,
  } = useInventory(productionTypeFilter, { who: is3pl ? whoToListInput(whoFilter) : undefined });
  const { locations, isLoading: locationsLoading } = useLocations();
  // BLU-711 A1: `selectedLocation` is a once-per-session snapshot from
  // LocationContext, so junction flags that flip mid-session (the 3PL owner
  // enabling client stock writes) never reach it. Re-resolve against the live
  // list for anything authority-bearing; fall back to the snapshot so a
  // still-loading list can't transiently look like "no location selected".
  const liveSelectedLocation = selectedLocation
    ? locations.find((loc) => loc.id === selectedLocation.id) ?? selectedLocation
    : null;
  // BLU-717 S3: pre-printing is a per-location permission, so the "Add
  // pre-printed stock" trigger has to know the answer BEFORE the operator fills
  // the modal (the backend rejects at submit otherwise). `null` = unknown (the
  // read is own-locations-only and coordinator-tier — see the hook's header);
  // unknown never gates.
  const preprintPermission = usePreprintPermission(liveSelectedLocation?.id, 'garment');
  const { profile } = useAuth();
  const { can } = useRole();
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
  const [isBulkStockUploadOpen, setIsBulkStockUploadOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  usePageTitle(t('title'), t('subtitle'));

  useEffect(() => {
    if (locationsLoading || orgLoading || !profile || !organization) return;
    if (!can('org_settings.manage')) return;
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    // The legacy first-run modal is Shopify-hardwired (its Connect-Shopify step
    // is required and unskippable). A pure-fulfilment 3PL provider never attaches
    // a store and sets up shared locations via the claim flow, not this wizard —
    // so suppress the modal entirely for them rather than trapping them on a
    // store-connect step they can't complete.
    if (organization.is_3pl_provider) {
      setShowOnboarding(false);
      return;
    }
    const needsShopifySetup = !organization.shopify_domain || organization.shopify_domain.trim() === '';
    const needsLocationSetup = locations.length === 0;
    setShowOnboarding(!onboardingCompleted && (needsShopifySetup || needsLocationSetup));
  }, [locations, locationsLoading, organization, orgLoading, profile, can]);

  const handleOnboardingComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowOnboarding(false);
  };

  // BLU-3xx Phase 47.01.2-B-04: extended signature carries an optional
  // `targetOrganizationId` (cross-org target) AND returns the
  // `transactionId` of the receipt so the modal can wire the 30-second
  // Undo toast to `inventory.reverseReceipt` (B-02).
  // BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved Product (not a
  // sku string) so cross-org targets work end-to-end. See useInventory
  // header comment.
  const handleAddStock = async (
    product: Product,
    quantity: number,
    receivedDate: Date,
    locationId?: string,
    targetOrganizationId?: string,
    reference?: string,
  ) => {
    const result = await addStock(product, quantity, receivedDate, locationId, targetOrganizationId, reference);
    if (result.success) {
      setIsAddStockModalOpen(false);
      return { success: true as const, transactionId: result.transactionId };
    }
    return { success: false as const };
  };

  const handleSetStock = async (
    product: Product,
    quantity: number,
    locationId?: string,
    targetOrganizationId?: string,
    reference?: string,
  ) => {
    return await setStock(
      product,
      quantity,
      undefined,
      undefined,
      undefined,
      undefined,
      locationId,
      targetOrganizationId,
      reference,
    );
  };

  // Only show the full-page skeleton on the initial load; keep the table visible on location switches.
  if ((loading && products.length === 0) || locationsLoading || orgLoading) {
    return <PageLoader pool="inventory" />;
  }

  if (error) {
    return (
      <div className="p-8">
        <PageErrorState title={t('errorLoading')} description={error.message} />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Full-width list-page padding (mirrors OrdersList). The responsive
          px-4 sm:px-6 lg:px-8 exactly cancels PageActionBar's
          -mx-4 sm:-mx-6 lg:-mx-8 breakout at every breakpoint — a flat px-6
          overshot the bar's lg:-mx-8 by 8px/side, forcing a spurious
          page-level horizontal scrollbar. */}
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <ErrorBoundary
          fallback={
            <div className="bg-card rounded-lg border p-8 text-center">
              <h3 className="text-lg font-semibold mb-2">{t('errorLoadingTable')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('errorLoadingTableDesc')}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                {t('reloadPage')}
              </button>
            </div>
          }
        >
          <InventoryTable
            products={products}
            onAddStock={() => setIsAddStockModalOpen(true)}
            onAddStockIndividual={() => setIsAddStockModalOpen(true)}
            onAddStockCsv={() => setIsBulkStockUploadOpen(true)}
            onSetStock={handleSetStock}
            onReconcile={can('inventory.write') ? () => navigate('/inventory/reconciliation') : undefined}
            loading={loading}
            downloadStockCsv={downloadStockCsv}
            moveToUnavailable={moveToUnavailable}
            moveToAvailable={moveToAvailable}
            onProductUpdated={refreshInventory}
            productionTypeFilter={productionTypeFilter}
            onProductionTypeFilterChange={setProductionTypeFilter}
            whoFilter={whoFilter}
            onWhoFilterChange={is3pl ? setWhoFilter : undefined}
            whoClients={whoClients}
            // BLU-3xx Phase 47.01.2-B-04: read-only state for client users at
            // 3PL locations (gate at trigger, not inside the modal). When the
            // active location is shared and the caller is the CLIENT, stock
            // actions are managed by the 3PL provider — disable + explain.
            // BLU-711 A1: unless the owner has opted this client in to writing
            // its own stock here, in which case the client may adjust the rows
            // it owns. Mirrors assertCallerManagesLocationStock, which is the
            // actual policy — a disabled button is not a policy (four-layer rule).
            // Read the flag off the LIVE locations list, not the selected
            // snapshot: `selectedLocation` is captured once per session, so an
            // owner enabling this mid-session would otherwise leave the client
            // disabled until a hard reload — including when they follow the
            // "you can now adjust your stock" notification straight to /inventory.
            stockActionsDisabled={
              !liveSelectedLocation ||
              (!!liveSelectedLocation.is_shared && !liveSelectedLocation.client_stock_writes_enabled)
            }
            stockActionsDisabledReason={
              liveSelectedLocation?.is_shared && !liveSelectedLocation.client_stock_writes_enabled
                ? 'Stock adjustments at this location are managed by your 3PL provider.'
                : undefined
            }
            // Pre-printing changes production behaviour at the OWNER's floor
            // (production_type flips to PAP), which the BLU-711 stock grant
            // deliberately does not cover — so it stays gated on is_shared.
            // BLU-717 S3 adds the second gate: the floor must be permitted to
            // pre-print at all. Two reasons, one disabled state, and the reason
            // copy below says WHICH one applies.
            prePrintActionsDisabled={
              !!liveSelectedLocation?.is_shared || preprintPermission.blocked
            }
            // 3PL takes precedence (a client cannot flip the owner's setting, so
            // naming the setting would be a dead end for them); passing no reason
            // falls back to the 3PL copy the filter bar owns.
            prePrintActionsDisabledReason={
              !liveSelectedLocation?.is_shared && preprintPermission.blocked
                ? `Pre-printing is not enabled at ${preprintPermission.locationName ?? liveSelectedLocation?.name ?? 'this location'}. Enable it in Production Settings → Pre-printing.`
                : undefined
            }
          />
        </ErrorBoundary>
      </div>

      <AddStockModal
        isOpen={isAddStockModalOpen}
        onClose={() => setIsAddStockModalOpen(false)}
        onAddStock={handleAddStock}
        products={products}
      />

      <BulkStockUploadModal
        isOpen={isBulkStockUploadOpen}
        onClose={() => setIsBulkStockUploadOpen(false)}
        onSuccess={refreshInventory}
      />

      <OnboardingModal
        open={showOnboarding}
        onComplete={handleOnboardingComplete}
      />

      <BackToTopPill />
    </div>
  );
};

// Wrap with error boundary for route-level error handling
export default function InventoryWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <Inventory />
    </PageErrorBoundary>
  );
}
